"use client";

import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import TrackedLink from "./TrackedLink";
import {
    NAV_SEARCH_CATEGORIES,
    getNavSearchResults,
    type NavSearchCategory,
} from "./nav-search-data";

type NavSearchPanelProps = {
    tone: "light" | "dark";
    variant: "desktop" | "mobile";
    panelId: string;
    query: string;
    category: NavSearchCategory;
    onCategoryChange: (category: NavSearchCategory) => void;
    onNavigate: () => void;
};

const NavSearchPanel = ({
    tone,
    variant,
    panelId,
    query,
    category,
    onCategoryChange,
    onNavigate,
}: NavSearchPanelProps) => {
    const results = getNavSearchResults(category, query);
    const heading = query.trim() ? "Results" : "Trending searches";
    const headingId = `${panelId}-heading`;
    const isDesktop = variant === "desktop";

    return (
        <div
            id={panelId}
            role="region"
            aria-labelledby={headingId}
            className={cn(
                "flex flex-col",
                isDesktop
                    ? "max-h-[480px] gap-10 overflow-y-auto rounded-b-[var(--radius-m)] p-5 shadow-[0_4px_15px_0_rgba(0,0,0,0.07)]"
                    : "gap-8",
                isDesktop && (tone === "light" ? "bg-white" : "bg-nav-bg"),
            )}
        >
            <div role="group" aria-label="Search categories" className="flex flex-wrap gap-5">
                {NAV_SEARCH_CATEGORIES.map((item) => {
                    const selected = item.id === category;
                    return (
                        <button
                            key={item.id}
                            type="button"
                            aria-pressed={selected}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => onCategoryChange(item.id)}
                            className={cn(
                                "inline-flex h-[30px] items-center justify-center rounded-[var(--radius-m)] border border-solid px-3 py-1 whitespace-nowrap transition-colors",
                                isDesktop
                                    ? "text-base leading-[22px] tracking-[-0.01em]"
                                    : "text-sm leading-5 tracking-[-0.01em]",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                                tone === "light"
                                    ? "focus-visible:ring-button-primary focus-visible:ring-offset-white"
                                    : "focus-visible:ring-white focus-visible:ring-offset-nav-bg",
                                selected
                                    ? tone === "light"
                                        ? "border-[#fde7d7] bg-[#fef5ee] font-medium text-brandAccent"
                                        : "border-[rgba(255,255,255,0.25)] bg-nav-fg font-medium text-white"
                                    : tone === "light"
                                      ? "border-badge-neutral-200 bg-badge-neutral-100 font-regular text-badge-neutral-500 hover:border-badge-neutral-300"
                                      : "border-nav-bg bg-nav-fg font-regular text-[rgba(255,255,255,0.5)] hover:text-[rgba(255,255,255,0.8)]",
                            )}
                        >
                            {item.label}
                        </button>
                    );
                })}
            </div>

            <div className="flex flex-col gap-5" aria-live="polite">
                <h2
                    id={headingId}
                    className={cn(
                        "font-semibold uppercase leading-[22px]",
                        isDesktop ? "text-base tracking-[6px]" : "text-base tracking-[2px]",
                        tone === "light" ? "text-ink-header" : "text-white",
                    )}
                >
                    {heading}
                </h2>
                {results.length === 0 ? (
                    <p
                        className={cn(
                            "px-4 text-base leading-[22px]",
                            tone === "light" ? "text-badge-neutral-500" : "text-[rgba(255,255,255,0.7)]",
                        )}
                    >
                        No matching products
                    </p>
                ) : (
                    <ul className="flex flex-col gap-5">
                        {results.map((item) => (
                            <li key={`${item.category}-${item.label}`}>
                                <TrackedLink
                                    href={item.href}
                                    target={item.openInNewTab ? "_blank" : undefined}
                                    rel={item.openInNewTab ? "noopener noreferrer" : undefined}
                                    onClick={onNavigate}
                                    className={cn(
                                        "flex items-center gap-3 rounded-[var(--radius-m)] px-4 py-3 no-underline transition-colors",
                                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                                        tone === "light"
                                            ? "text-ink-body hover:bg-badge-neutral-100 focus-visible:bg-badge-neutral-100 focus-visible:ring-button-primary focus-visible:ring-offset-white"
                                            : "text-white hover:bg-nav-fg focus-visible:bg-nav-fg focus-visible:ring-white focus-visible:ring-offset-nav-bg",
                                        isDesktop
                                            ? "text-lg font-regular leading-6 hover:font-bold focus-visible:font-bold"
                                            : "text-lg font-regular leading-6 hover:font-semibold focus-visible:font-semibold",
                                    )}
                                >
                                    <TrendingUp
                                        className="size-6 shrink-0"
                                        strokeWidth={1.5}
                                        aria-hidden
                                    />
                                    <span>{item.label}</span>
                                </TrackedLink>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default NavSearchPanel;
